import React, { useContext } from 'react';
import { Text, Label, Icon, IconButton } from '@fluentui/react';
import { ThemeContext } from '../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../Common/util/localStorageUtils';
import {
    EnvironmentData,
    EnvironmentFlags,
    Pipeline,
    Region,
    WorkLane,
} from '../models/EnvironmentModels';

interface ActiveConfigurationPreviewProps {
    environmentData: EnvironmentData;
    isExpanded: boolean;
    onToggleExpand: () => void;
}

interface ActiveModeConfig {
    isEnabled: boolean;
    region: number | null | undefined;
    pipeline: number | null | undefined;
    worklane: number | null | undefined;
    featureFlags: EnvironmentFlags;
    isOverridden: boolean;
    overriddenFields?: {
        isEnabled: boolean;
        region: boolean;
        pipeline: boolean;
        worklane: boolean;
        featureFlags: Set<string>;
    };
}

export const ActiveConfigurationPreview: React.FC<ActiveConfigurationPreviewProps> =
    ({ environmentData, isExpanded, onToggleExpand }) => {
        const themeContext = useContext(ThemeContext);
        const theme = getThemeFromString(themeContext.themeName);

        const getActiveModeConfig = (): ActiveModeConfig | null => {
            if (!environmentData) return null;

            const activeModeName =
                environmentData.activeOperationalModeName || 'Normal';
            const normalMode = environmentData.normalOperationMode;

            if (activeModeName === 'Normal') {
                return {
                    isEnabled: normalMode.isEnabled,
                    region: normalMode.region,
                    pipeline: normalMode.pipeline,
                    worklane: normalMode.worklane,
                    featureFlags: normalMode.featureFlags,
                    isOverridden: false,
                };
            }

            const activeMode = environmentData.operationModes.find(
                (m) => m.name === activeModeName
            );
            if (!activeMode) {
                return {
                    isEnabled: normalMode.isEnabled,
                    region: normalMode.region,
                    pipeline: normalMode.pipeline,
                    worklane: normalMode.worklane,
                    featureFlags: normalMode.featureFlags,
                    isOverridden: false,
                };
            }

            // Merge feature flags: active mode values override normal mode values (only if not null/undefined)
            const mergedFlags: EnvironmentFlags = {};
            const overriddenFlags: Set<string> = new Set();
            const flagKeys = Object.keys(normalMode.featureFlags) as Array<
                keyof EnvironmentFlags
            >;
            flagKeys.forEach((key) => {
                const activeModeValue = activeMode.featureFlags[key];
                const normalModeValue = normalMode.featureFlags[key];
                mergedFlags[key] =
                    activeModeValue !== undefined && activeModeValue !== null
                        ? activeModeValue
                        : normalModeValue;
                if (activeModeValue !== undefined && activeModeValue !== null) {
                    overriddenFlags.add(key);
                }
            });

            // Track which fields are overridden
            const overriddenFields = {
                isEnabled:
                    activeMode.isEnabled !== undefined &&
                    activeMode.isEnabled !== null,
                region:
                    activeMode.region !== undefined &&
                    activeMode.region !== null,
                pipeline:
                    activeMode.pipeline !== undefined &&
                    activeMode.pipeline !== null,
                worklane:
                    activeMode.worklane !== undefined &&
                    activeMode.worklane !== null,
                featureFlags: overriddenFlags,
            };

            // Merge: active mode values override normal mode values (only if not null)
            return {
                isEnabled:
                    activeMode.isEnabled !== undefined &&
                    activeMode.isEnabled !== null
                        ? activeMode.isEnabled
                        : normalMode.isEnabled,
                region:
                    activeMode.region !== undefined &&
                    activeMode.region !== null
                        ? activeMode.region
                        : normalMode.region,
                pipeline:
                    activeMode.pipeline !== undefined &&
                    activeMode.pipeline !== null
                        ? activeMode.pipeline
                        : normalMode.pipeline,
                worklane:
                    activeMode.worklane !== undefined &&
                    activeMode.worklane !== null
                        ? activeMode.worklane
                        : normalMode.worklane,
                featureFlags: mergedFlags,
                isOverridden: true,
                overriddenFields,
            };
        };

        const activeConfig = getActiveModeConfig();
        if (!activeConfig) return null;

        const isInNormalMode = !activeConfig.isOverridden;
        const overriddenFields = activeConfig.overriddenFields;
        const flagEntries = Object.entries(activeConfig.featureFlags) as [
            keyof EnvironmentFlags,
            boolean | null | undefined
        ][];

        return (
            <div
                style={{
                    marginTop: '16px',
                    backgroundColor: theme.palette.white,
                    border: `2px solid ${theme.palette.neutralSecondary}`,
                    borderRadius: '4px',
                    overflow: 'hidden',
                }}
            >
                {/* Collapsible Header */}
                <div
                    style={{
                        padding: '12px 16px',
                        backgroundColor: theme.palette.neutralLight,
                        borderBottom: `1px solid ${theme.palette.neutralSecondary}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}
                    onClick={onToggleExpand}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <Icon
                            iconName={
                                isInNormalMode
                                    ? 'StatusCircleCheckmark'
                                    : 'Warning'
                            }
                            styles={{
                                root: {
                                    color: isInNormalMode
                                        ? theme.palette.green
                                        : theme.palette.orangeLight,
                                    fontSize: 20,
                                },
                            }}
                        />
                        <Text
                            variant="large"
                            styles={{ root: { fontWeight: 600 } }}
                        >
                            Active Configuration Preview
                            {!isInNormalMode && (
                                <span
                                    style={{
                                        color: theme.palette.orangeLight,
                                        marginLeft: '8px',
                                        fontWeight: 600,
                                    }}
                                >
                                    (Override Mode Active)
                                </span>
                            )}
                        </Text>
                    </div>
                    <IconButton
                        iconProps={{
                            iconName: isExpanded ? 'ChevronUp' : 'ChevronDown',
                        }}
                        title={isExpanded ? 'Collapse' : 'Expand'}
                        ariaLabel={
                            isExpanded
                                ? 'Collapse active configuration preview'
                                : 'Expand active configuration preview'
                        }
                        aria-expanded={isExpanded}
                        styles={{ root: { height: '32px' } }}
                    />
                </div>

                {/* Content */}
                {isExpanded && (
                    <div style={{ padding: '16px' }}>
                        {/* Mode Configuration Section */}
                        <div style={{ marginBottom: '16px' }}>
                            <Label
                                styles={{
                                    root: {
                                        fontWeight: 600,
                                        marginBottom: '8px',
                                    },
                                }}
                            >
                                Mode Configuration
                            </Label>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns:
                                        'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '12px',
                                }}
                            >
                                {/* Enabled Field */}
                                <div
                                    style={{
                                        padding: '12px',
                                        backgroundColor:
                                            theme.palette.neutralLighter,
                                        borderTop: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderRight: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderBottom: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderLeft: overriddenFields?.isEnabled
                                            ? `4px solid ${theme.palette.orangeLight}`
                                            : `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderRadius: '4px',
                                    }}
                                >
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                fontWeight: 600,
                                                display: 'block',
                                                marginBottom: '4px',
                                            },
                                        }}
                                    >
                                        Enabled
                                        {overriddenFields?.isEnabled && (
                                            <Icon
                                                iconName="Edit"
                                                styles={{
                                                    root: {
                                                        marginLeft: '4px',
                                                        fontSize: 12,
                                                        color: theme.palette
                                                            .orangeLight,
                                                    },
                                                }}
                                            />
                                        )}
                                    </Text>
                                    <Text
                                        variant="medium"
                                        styles={{
                                            root: {
                                                color: activeConfig.isEnabled
                                                    ? theme.palette.green
                                                    : theme.palette.redDark,
                                                fontWeight: 600,
                                            },
                                        }}
                                    >
                                        {activeConfig.isEnabled ? 'Yes' : 'No'}
                                    </Text>
                                </div>

                                {/* Pipeline Field */}
                                <div
                                    style={{
                                        padding: '12px',
                                        backgroundColor:
                                            theme.palette.neutralLighter,
                                        borderTop: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderRight: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderBottom: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderLeft: overriddenFields?.pipeline
                                            ? `4px solid ${theme.palette.orangeLight}`
                                            : `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderRadius: '4px',
                                    }}
                                >
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                fontWeight: 600,
                                                display: 'block',
                                                marginBottom: '4px',
                                            },
                                        }}
                                    >
                                        Pipeline
                                        {overriddenFields?.pipeline && (
                                            <Icon
                                                iconName="Edit"
                                                styles={{
                                                    root: {
                                                        marginLeft: '4px',
                                                        fontSize: 12,
                                                        color: theme.palette
                                                            .orangeLight,
                                                    },
                                                }}
                                            />
                                        )}
                                    </Text>
                                    <Text variant="medium">
                                        {activeConfig.pipeline !== null &&
                                        activeConfig.pipeline !== undefined
                                            ? Pipeline[activeConfig.pipeline]
                                            : 'Not Set'}
                                    </Text>
                                </div>

                                {/* Region Field */}
                                <div
                                    style={{
                                        padding: '12px',
                                        backgroundColor:
                                            theme.palette.neutralLighter,
                                        borderTop: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderRight: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderBottom: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderLeft: overriddenFields?.region
                                            ? `4px solid ${theme.palette.orangeLight}`
                                            : `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderRadius: '4px',
                                    }}
                                >
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                fontWeight: 600,
                                                display: 'block',
                                                marginBottom: '4px',
                                            },
                                        }}
                                    >
                                        Region
                                        {overriddenFields?.region && (
                                            <Icon
                                                iconName="Edit"
                                                styles={{
                                                    root: {
                                                        marginLeft: '4px',
                                                        fontSize: 12,
                                                        color: theme.palette
                                                            .orangeLight,
                                                    },
                                                }}
                                            />
                                        )}
                                    </Text>
                                    <Text variant="medium">
                                        {activeConfig.region !== null &&
                                        activeConfig.region !== undefined
                                            ? Region[activeConfig.region]
                                            : 'Not Set'}
                                    </Text>
                                </div>

                                {/* Work Lane Field */}
                                <div
                                    style={{
                                        padding: '12px',
                                        backgroundColor:
                                            theme.palette.neutralLighter,
                                        borderTop: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderRight: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderBottom: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderLeft: overriddenFields?.worklane
                                            ? `4px solid ${theme.palette.orangeLight}`
                                            : `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                        borderRadius: '4px',
                                    }}
                                >
                                    <Text
                                        variant="small"
                                        styles={{
                                            root: {
                                                fontWeight: 600,
                                                display: 'block',
                                                marginBottom: '4px',
                                            },
                                        }}
                                    >
                                        Work Lane
                                        {overriddenFields?.worklane && (
                                            <Icon
                                                iconName="Edit"
                                                styles={{
                                                    root: {
                                                        marginLeft: '4px',
                                                        fontSize: 12,
                                                        color: theme.palette
                                                            .orangeLight,
                                                    },
                                                }}
                                            />
                                        )}
                                    </Text>
                                    <Text variant="medium">
                                        {activeConfig.worklane !== null &&
                                        activeConfig.worklane !== undefined
                                            ? WorkLane[activeConfig.worklane]
                                            : 'Not Set'}
                                    </Text>
                                </div>
                            </div>
                        </div>

                        {/* Feature Flags Section */}
                        <div>
                            <Label
                                styles={{
                                    root: {
                                        fontWeight: 600,
                                        marginBottom: '8px',
                                    },
                                }}
                            >
                                Feature Flags
                            </Label>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns:
                                        'repeat(auto-fill, minmax(200px, 1fr))',
                                    gap: '12px',
                                }}
                            >
                                {flagEntries.map(([key, value]) => {
                                    const displayName = key
                                        .replace(/([A-Z])/g, ' $1')
                                        .trim();
                                    const capitalizedName =
                                        displayName.charAt(0).toUpperCase() +
                                        displayName.slice(1);
                                    const isOverridden =
                                        overriddenFields?.featureFlags?.has(
                                            key
                                        );

                                    return (
                                        <div
                                            key={key}
                                            style={{
                                                padding: '12px',
                                                backgroundColor:
                                                    theme.palette
                                                        .neutralLighter,
                                                borderTop: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                                borderRight: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                                borderBottom: `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                                borderLeft: isOverridden
                                                    ? `4px solid ${theme.palette.orangeLight}`
                                                    : `1px solid ${theme.palette.neutralTertiaryAlt}`,
                                                borderRadius: '4px',
                                            }}
                                        >
                                            <Text
                                                variant="small"
                                                styles={{
                                                    root: {
                                                        fontWeight: 600,
                                                        display: 'block',
                                                        marginBottom: '4px',
                                                    },
                                                }}
                                            >
                                                {capitalizedName}
                                                {isOverridden && (
                                                    <Icon
                                                        iconName="Edit"
                                                        styles={{
                                                            root: {
                                                                marginLeft:
                                                                    '4px',
                                                                fontSize: 12,
                                                                color: theme
                                                                    .palette
                                                                    .orangeLight,
                                                            },
                                                        }}
                                                    />
                                                )}
                                            </Text>
                                            <Text
                                                variant="medium"
                                                styles={{
                                                    root: {
                                                        color:
                                                            value === true
                                                                ? theme.palette
                                                                      .green
                                                                : value ===
                                                                  false
                                                                ? theme.palette
                                                                      .redDark
                                                                : theme.palette
                                                                      .neutralSecondary,
                                                        fontWeight: 600,
                                                    },
                                                }}
                                            >
                                                {value === true
                                                    ? 'True'
                                                    : value === false
                                                    ? 'False'
                                                    : 'Not Set'}
                                            </Text>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };
